import { BRAND_NAME } from "../../constants/brand";
import { Button, Grid, Typography, Box, Chip, IconButton, Divider } from "@mui/material"
import { useState, useEffect, useRef, type ReactNode } from "react"

import homePageStyle from '../../Styles/ComponentStyles/LandingPage/HomePage.module.scss'

import IULiconForScrollCard from "../../assets/Images/Home/IULiconForScrollCard.svg"
import rothIconForScrollCard from "../../assets/Images/rothIconForScrollCards.svg"
import incomeIconForScrollCard from "../../assets/Images/incomeIconForScrollCard.svg"
import accumIconForScrollFCard from "../../assets/Images/accumIconForScrollFCard.svg"

import circleTickIcon from "../../assets/Images/circleTickIcon.svg"
import unfilledBrainLogo from "../../assets/Images/unfilledBrain.svg"
import homePageStrategyImage from "../../assets/Images/homePageStrategyImage.png"

import IconsColor from "../../Utils/IconsColor"
import GlobalPopUp from "../../GlobalComponent/GlobalPopup/GlobalPopup"
import ScheduleADemo from "../../GlobalComponent/ScheduleADemo/ScheduleADemo"

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";

const appendSliderDots = (dots: ReactNode) => (
    <div className={homePageStyle.dotcontainer}>
        <ul className={homePageStyle.chatbotdots}>{dots}</ul>
    </div>
)

const HomePage = () => {

    const cardStackRef = useRef<HTMLDivElement>(null)
    const stackSectionRef = useRef<HTMLDivElement>(null)
    const cardRefs = useRef<(HTMLDivElement | null)[]>([])
    const [flagOpenScheduleDemoDialog, setFlagOpenScheduleDemoDialog] = useState(false)
    const [isTabletOrBelow, setIsTabletOrBelow] = useState<boolean>(window.innerWidth <= 1024)

    // Refs for CodyHouse logic
    const ioRef = useRef<IntersectionObserver | null>(null)
    const scrollListenerRef = useRef<((this: Window, ev: Event) => any) | null>(null)
    const resizingIdRef = useRef<any>(null)
    const headerRef = useRef<HTMLDivElement | null>(null)

    const cardData = [
        {
            id: 'iul',
            title: 'IUL',
            description: 'Indexed Universal Life Insurance offering tax-free growth potential with downside protection and flexible premium payments.',
            icon: <img src={IULiconForScrollCard} alt={"icon"} style={{ width: "100%", height: "100%" }} />,
            features: ['0% Downside Floor', 'Tax-Free Withdrawals', 'Flexible Premiums', 'Built-In Legacy Benefit'],
            tabs: ['Indexed Returns', 'Financial Security'],
            sections: [
                { title: 'See the Bigger Picture', content: 'Run side-by-side comparisons between IUL and traditional retirement plans to clearly show how market floors, caps, and fees impact long-term growth.' },
                { title: 'Real Numbers, Personalized to You', content: 'Enter just a few details and instantly see how IUL can grow your wealth, create tax-free retirement income, and provide lifelong protection.' },
                { title: 'Visualize Growth & Protection', content: 'Our charts show how IUL shields you from market downturns while still capturing upside growth, so clients can see the advantage, not just hear about it.' },
                { title: 'Understand the True Costs & Benefits', content: 'Compare fees, taxes, and income across scenarios, see exactly how much more efficient IUL can be over time.' },
            ],
            color: '#3B78CE1A',
            arrowColor: '#3B78CE',
            cardBgColor: "#F4F9FF"
        },
        {
            id: 'roth',
            title: 'Annuity-Roth',
            description: 'A Tax-Free Growth Strategy for Retirement with Guaranteed Income',
            icon: <img src={rothIconForScrollCard} alt={"icon"} />,
            features: ['Tax-Free Retirement Income', 'Lifetime Income Guarantee', 'No Contribution Limits', 'Flexible Withdrawal Options'],
            tabs: ['Flexible Access', 'Lifetime Income'],
            sections: [
                { title: 'Maximize Your Retirement Savings', content: 'Learn how Annuity-Roth helps you build wealth for retirement while minimizing tax liabilities.' },
                { title: 'Customized Projections for Your Future', content: 'Input your goals to see how Annuity-Roth can generate a steady income stream for you in retirement.' },
                { title: 'Growth with Safety and Security', content: 'With tax-free growth and no risk to your principal, your retirement savings grow safely.' },
                { title: 'Understand Your Retirement Strategy', content: 'Compare projected income, fees, and growth scenarios to understand how Annuity-Roth supports retirement goals.' },
            ],
            color: '#00D23F1A',
            arrowColor: '#00D23F',
            cardBgColor: "#F4F9FF"
        },
        {
            id: 'income',
            title: 'Annuity-Income',
            description: 'Guaranteed, Steady Income for Life—A Reliable Solution for Your Retirement',
            icon: <img src={incomeIconForScrollCard} alt={"icon"} />,
            features: ['Guaranteed Lifetime Income', 'Tax-Deferred Growth', 'Flexible Payout Options', 'Protection from Market Fluctuations'],
            tabs: ['Guaranteed Income', 'Lifetime Payouts'],
            sections: [
                {
                    title: 'Plan for a Secure Future', content: 'See how an Annuity Income plan can provide reliable, long-term income, designed to support your lifestyle throughout retirement.'
                },
                {
                    title: 'Personalized Income Projections', content: 'Input your retirement goals to see how Annuity Income can guarantee you the exact income you need for the years to come.'
                },
                {
                    title: 'Financial Freedom in Retirement', content: 'With guaranteed payouts and tax-deferred growth, you can plan confidently, knowing your financial future is secure.'
                },
                {
                    title: 'Maximize Your Retirement Benefits', content: 'Compare different payout options, tax advantages, and income scenarios to determine the most efficient Annuity Income strategy for your needs.'
                },
            ],
            color: '#F6DA3A1A',
            arrowColor: '#F6DA3A',
            cardBgColor: "#F7FFF4"
        },
        {
            id: 'accumulation',
            title: 'Annuity-Accumulation',
            description: 'A Flexible Savings Option to Grow Your Retirement Fund with Tax-Deferred Growth',
            icon: <img src={accumIconForScrollFCard} alt={"icon"} />,
            features: ['Tax-Deferred Growth', 'Flexible Contributions', 'Protection from Market Volatility', 'Customizable Payout Options'],
            tabs: ['Flexible Contributions', 'Principal Protection'],
            sections: [
                { title: 'Optimize Your Retirement Savings', content: 'See how an Annuity Accumulation strategy can help you save for retirement with tax-deferred growth and steady contributions.' },
                { title: 'Projected Growth for Your Goals', content: 'Enter your contribution details and view how an Annuity Accumulation can help you grow your savings over time.' },
                { title: 'Security with Flexibility', content: 'Enjoy the safety of a guaranteed return and the flexibility to withdraw when you need it most, without market risk.' },
                { title: 'Visualize Your Financial Future', content: 'Compare different scenarios to understand how Annuity Accumulation can best help you achieve your long-term retirement goals.' },
            ],
            color: '#5E336C1A',
            arrowColor: '#5E336C',
            cardBgColor: "#F4F1F5"
        }
    ]

    const features = [
        {
            title: "Income Gap Supplementation",
            description: "Quickly spot the gap between needs and income and show how IUL fills it with tax-free dollars."
        },
        {
            title: "Roth Conversion",
            description: "Turn tax-deferred accounts into tax-free strategies with IUL modeled as a Roth alternative."
        },
        {
            title: "Accumulation",
            description: "Show clients how IUL grows safely with 0% downside protection and steady compounding."
        },
        {
            title: "Annuity Income Plan",
            description: "Provide clients with a guaranteed income stream for life, protecting against outliving their savings with steady, reliable payouts."
        },
        {
            title: "Inflation Rate Calculator",
            description: "Project real retirement income after inflation and protect purchasing power with IUL."
        },
        {
            title: "Qualified Account Spend Down",
            description: "Reveal how fast traditional accounts run dry in your retirement."
        }
    ];

    const handleScheduleDemoBtnClick = () => {
        // setFlagOpenScheduleDemoDialog(true)
        window.open("https://calendly.com/marketing-sales-brokersalliance/30min", "_blank", "noopener,noreferrer")
    }

    const handleLearnMoreClick = () => {
        const retirementSection = document.getElementById('retirement-strategy-section')
        if (retirementSection) {
            retirementSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }

    useEffect(() => {
        const onResize = () => setIsTabletOrBelow(window.innerWidth <= 1024)
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    useEffect(() => {
        if (isTabletOrBelow) return
        const containerEl = cardStackRef.current
        const items = cardRefs.current.filter(Boolean) as HTMLDivElement[]
        const headerEl = headerRef.current
        if (!containerEl || items.length === 0) return

        let marginY = 0
        let elementHeight = 0
        let cardTop = 0
        let cardHeight = 0
        let windowHeight = window.innerHeight
        let ticking = false

        const getIntegerFromProperty = (cssHeight: string) => {
            const node = document.createElement('div')
            node.setAttribute('style', `opacity:0; visibility:hidden; position:absolute; height:${cssHeight}`)
            containerEl.appendChild(node)
            const val = Number.parseInt(getComputedStyle(node).getPropertyValue('height'))
            return val
        }

        const setStackCards = () => {
            const gapProp = getComputedStyle(containerEl).getPropertyValue('--stack-cards-gap')
            marginY = getIntegerFromProperty(gapProp)
            elementHeight = containerEl.offsetHeight

            const firstItem = items[0]
            const style = getComputedStyle(firstItem)
            cardTop = Math.floor(Number.parseFloat(style.getPropertyValue('top')))
            cardHeight = Math.floor(Number.parseFloat(style.getPropertyValue('height')))

            windowHeight = window.innerHeight

            // reset padding bottom to allow scrolling through stacked items
            containerEl.style.paddingBottom = Number.isNaN(marginY as any) ? '0px' : `${marginY * (items.length - 1)}px`

            items.forEach((it, i) => {
                if (Number.isNaN(marginY as any)) {
                    it.style.transform = 'none'
                } else {
                    it.style.transform = `translateY(${marginY * i}px)`
                }
            })
        }

        const animate = () => {
            if (Number.isNaN(marginY as any)) {
                ticking = false
                return
            }

            const top = containerEl.getBoundingClientRect().top
            if (cardTop - top + windowHeight - elementHeight - cardHeight + marginY + marginY * items.length > 0) {
                ticking = false
                return
            }

            for (let i = 0; i < items.length; i++) {
                const scrolling = cardTop - top - i * (cardHeight + marginY)
                if (scrolling > 0) {
                    const scaling = i === items.length - 1 ? 1 : (cardHeight - scrolling * 0.05) / cardHeight
                    items[i].style.transform = `translateY(${marginY * i}px) scale(${scaling})`
                } else {
                    items[i].style.transform = `translateY(${marginY * i}px)`
                }
            }

            ticking = false
        }

        const onScroll = () => {
            if (ticking) return
            ticking = true
            window.requestAnimationFrame(animate)
        }

        const onResize = () => {
            clearTimeout(resizingIdRef.current)
            resizingIdRef.current = setTimeout(() => {
                setStackCards()
                animate()
            }, 300)
        }

        // IntersectionObserver to add/remove scroll listener like reference
        ioRef.current = new IntersectionObserver((entries) => {
            const entry = entries[0]
            if (entry.isIntersecting) {
                if (!scrollListenerRef.current) {
                    scrollListenerRef.current = onScroll
                    window.addEventListener('scroll', onScroll, { passive: true })
                }
                // activate sticky header while in view
                if (headerEl) headerEl.classList.add(homePageStyle.scrollHeaderStickyActive)
            } else {
                if (scrollListenerRef.current) {
                    window.removeEventListener('scroll', scrollListenerRef.current)
                    scrollListenerRef.current = null
                }
                // deactivate sticky header once out of view
                if (headerEl) headerEl.classList.remove(homePageStyle.scrollHeaderStickyActive)
            }
        }, { threshold: [0, 1] })

        ioRef.current.observe(containerEl)
        setStackCards()
        animate()
        window.addEventListener('resize', onResize)

        return () => {
            if (ioRef.current) {
                ioRef.current.disconnect()
                ioRef.current = null
            }
            if (scrollListenerRef.current) {
                window.removeEventListener('scroll', scrollListenerRef.current)
                scrollListenerRef.current = null
            }
            window.removeEventListener('resize', onResize)
            if (headerEl) headerEl.classList.remove(homePageStyle.scrollHeaderStickyActive)
        }
    }, [isTabletOrBelow])

    return (
        <Grid container>

            {flagOpenScheduleDemoDialog &&
                <GlobalPopUp
                    open={flagOpenScheduleDemoDialog}
                    onClose={() => setFlagOpenScheduleDemoDialog(false)}
                    flagDivider={true}
                    title={"Schedule a Demo"}
                    paperProps={{
                        sx: {
                            width: "800px"
                        }
                    }}
                >
                    <ScheduleADemo />
                </GlobalPopUp>

            }

            {/* Hero Video Section */}
            <Grid item xs={12} className={homePageStyle.heroSection}>
                <video
                    className={homePageStyle.heroVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                >
                    <source
                        src={"https://storage.googleapis.com/goall-agency/RetirementBrain/Video_heroVideo.mp4"}
                        type="video/mp4"
                    />
                </video>
                <div className={homePageStyle.heroOverlay}></div>
                <Grid container className={homePageStyle.heroContent}>
                    <Grid item xs={12}>
                        <Typography className={homePageStyle.heroTitle}>
                            Smarter Retirement Planning.
                        </Typography>
                        <Typography className={homePageStyle.heroTitle}>
                            Powered by One Tool.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} className={homePageStyle.heroSubtitleWrapper}>
                        <Typography className={homePageStyle.heroSubtitle}>
                            From income planning to product comparisons,
                        </Typography>
                        <Typography className={homePageStyle.heroSubtitle}>
                            the tool you need to build clarity and close with confidence.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} className={homePageStyle.ctaWrapper}>
                        <Grid container gap={2}>
                            <Grid item>
                                <Button
                                    className={homePageStyle.reqDemoCtaButton}
                                    onClick={handleScheduleDemoBtnClick}
                                >
                                    Schedule a Demo
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    className={homePageStyle.learnMoreCtaButtom}
                                    onClick={handleLearnMoreClick}
                                >
                                    Learn More
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Gradient Divider Section */}
            <Grid item xs={12}>
                <Grid container className={homePageStyle.gradientDividerSection}>
                    <Grid item>
                        <img src={unfilledBrainLogo} alt={"rbLogo"} width={"70px"} height={"60px"} />
                    </Grid>
                    <Grid item>
                        <Typography className={homePageStyle.gradientContent}>We built {BRAND_NAME} to help advisors cut through complexity</Typography>
                        <Typography className={homePageStyle.gradientContent}>with instant, client-ready strategies.</Typography>
                    </Grid>
                </Grid>
            </Grid>

            {/* Retirement Strategy Content Section */}
            <Grid item xs={12} className={homePageStyle.sectionContainer} id="retirement-strategy-section">
                <Grid container className={homePageStyle.retirementStrategySection}>
                    <Grid item xl={9} lg={11.5} md={11.5} sm={12} xs={12} className={homePageStyle.retirementStrategyContent}>
                        <Grid container gap={1}>
                            <Grid item xs={12}>
                                <Typography className={homePageStyle.retirementStrategySubtitle}>
                                    More Than Software. More Than Products.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography className={homePageStyle.retirementStrategyTitle}>
                                    A Complete Retirement Strategy.
                                </Typography>
                            </Grid>
                            <Grid item xl={9} lg={9} md={9} sm={11} xs={12} sx={{ margin: "0 auto !important" }}>
                                <Typography className={homePageStyle.retirementStrategyDescription}>
                                    {BRAND_NAME} is a powerful tool that combines interactive financial calculators with innovative products.
                                    It gives agents a client-facing platform that turns complex retirement planning into simple, visual storytelling.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xl={9} lg={11.5} md={11.5} sm={12} xs={12} className={homePageStyle.retirementStrategyImage}>
                        <Grid container sx={{ gap: "20px", justifyContent: 'space-between', alignItems: "center" }}>
                            <Grid item xl={5.8} lg={5.8} md={5.8} sm={12} xs={12} className={homePageStyle.dashboardRetirementItem}>
                                <Box className={homePageStyle.dashboardPreview}>
                                    <img
                                        src={homePageStrategyImage}
                                        alt={`${BRAND_NAME} Dashboard Preview`}
                                        className={homePageStyle.dashboardImage}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xl={5.8} lg={5.8} md={5.8} sm={12} xs={12}>
                                <Grid container className={homePageStyle.retirementStrategyFeatures}>
                                    <Grid item xs={12}>
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <Typography className={homePageStyle.retirementStrategyFeaturesTitle}>
                                                    On any device. In any meeting.
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography className={homePageStyle.retirementStrategyFeaturesSubtitle}>
                                                    {BRAND_NAME} allows you to:
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid container className={homePageStyle.featuresList}>
                                            <Grid item xs={12}>
                                                <Grid container className={homePageStyle.featureItem}>
                                                    <Grid item className={homePageStyle.arrowItem}>
                                                        <Box className={homePageStyle.featureArrow}>▶</Box>
                                                    </Grid>
                                                    <Grid item className={homePageStyle.arrowTextItem}>
                                                        <Typography className={homePageStyle.featureText}>
                                                            Compare current plans side by side with IUL.
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Grid container className={homePageStyle.featureItem}>
                                                    <Grid item className={homePageStyle.arrowItem}>
                                                        <Box className={homePageStyle.featureArrow}>▶</Box>
                                                    </Grid>
                                                    <Grid item className={homePageStyle.arrowTextItem}>
                                                        <Typography className={homePageStyle.featureText}>
                                                            Model tax savings, inflation impact, and fee reductions.
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Grid container className={homePageStyle.featureItem}>
                                                    <Grid item className={homePageStyle.arrowItem}>
                                                        <Box className={homePageStyle.featureArrow}>▶</Box>
                                                    </Grid>
                                                    <Grid item className={homePageStyle.arrowTextItem}>
                                                        <Typography className={homePageStyle.featureText}>
                                                            Show clients how their money lasts up to age 100.
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Grid container className={homePageStyle.featureItem}>
                                                    <Grid item className={homePageStyle.arrowItem}>
                                                        <Box className={homePageStyle.featureArrow}>▶</Box>
                                                    </Grid>
                                                    <Grid item className={homePageStyle.arrowTextItem}>
                                                        <Typography className={homePageStyle.featureText}>
                                                            Share secure links so clients can explore their analysis at home.
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid container className={homePageStyle.retirementStrategyCTA} spacing={2}>
                                            <Grid item>
                                                <Button
                                                    className={homePageStyle.requestDemoBtn}
                                                    onClick={handleScheduleDemoBtnClick}
                                                >
                                                    Schedule a Demo
                                                </Button>
                                            </Grid>
                                            {/* <Grid item>
                                                    <Button 
                                                    className={homePageStyle.learnMoreBtn}
                                                    >
                                                        Learn More
                                                    </Button>
                                                </Grid> */}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Features Section */}
            <Grid item xs={12}>
                <Grid container className={homePageStyle.featureSectionContainer}>
                    <Grid item xs={9}>
                        <Grid container>
                            <Grid item xs={12}>
                                <Typography className={homePageStyle.featureSectionTitle}>Features</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography className={homePageStyle.featureSectionSubtitle}>Everything You Need in One Tool.</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={9}>
                        <Grid container className={homePageStyle.featureCardsContainer}>
                            {features?.map((cardContent) => {
                                return (
                                    <Grid key={cardContent.title} item className={homePageStyle.featureCardContentContainer}>
                                        <Grid container gap={1}>
                                            <Grid item xs={12}>
                                                <Grid container gap={1} alignItems={"center"}>
                                                    <Grid item>
                                                        <IconButton className={homePageStyle.featureCardArrowStyle}>
                                                            <IconsColor iconName="arrowIconForCard" color="#316153" width={"15"} height={"15"} />
                                                        </IconButton>
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography className={homePageStyle.featureCardTitle}>{cardContent?.title}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography className={homePageStyle.featureCardDescription}>
                                                    {cardContent?.description}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )
                            })
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Card Stack / Carousel Section */}
            <Grid item xs={12} className={homePageStyle.scrollSection} ref={stackSectionRef}>
                <Grid container className={homePageStyle.scrollSectionMainContainer}>
                    <Grid item xl={12} lg={12} md={12} sm={11.5} xs={11.5} ref={headerRef} className={`${homePageStyle.scrollHeader}`}>
                        <Grid container>
                            <Grid item xs={12}>
                                <Typography className={homePageStyle.scrollSectionTitle}>Tailored Strategies for Every Retirement Goal</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography className={homePageStyle.scrollSectionSubTitle}>Empowering smarter retirement decisions through personalized choices, intelligent analysis, and efficient planning.</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    {isTabletOrBelow ? (
                        <>
                            {/* <Grid item xs={12} className={homePageStyle.cardsCarouselContainer}>
                                    <div className={homePageStyle.cardsCarouselHeader}>
                                        <IconButton size="small" className={homePageStyle.navIconBtn} onClick={() => scrollCardsCarousel('left')}>
                                            <ChevronLeftRoundedIcon />
                                        </IconButton>
                                        <IconButton size="small" className={homePageStyle.navIconBtnPrimary} onClick={() => scrollCardsCarousel('right')}>
                                            <ChevronRightRoundedIcon />
                                        </IconButton>
                                    </div>
                                    <div className={homePageStyle.cardsCarouselTrack} ref={cardsCarouselRef}>
                                        {cardData.map((card) => (
                                            <div key={card.id} className={homePageStyle.cardsCarouselItem}>
                                                <div className={homePageStyle.card}>
                                                    <div className={homePageStyle.cardHeader}>
                                                        <div className={homePageStyle.cardIcon}>{card.icon}</div>
                                                        <Grid container className={homePageStyle.cardTitleSection}>
                                                            <Grid item xs={12}>
                                                                <Typography className={homePageStyle.cardTitle}>{card.title}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography className={homePageStyle.cardDescription}>{card.description}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </div>
                                                    <div className={homePageStyle.solutionTabs}>
                                                        {(card.tabs || []).map((label, i) => (
                                                            <Chip key={i} label={label} className={homePageStyle.solutionChip} sx={{ background: card.color }} />
                                                        ))}
                                                    </div>
                                                    <div className={homePageStyle.cardContent}>
                                                        <Grid container className={homePageStyle.cardContent}>
                                                            <Grid item xs={12} className={homePageStyle.leftColumn}>
                                                                <Typography className={homePageStyle.featuresTitle}>Key Features</Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Grid container spacing={1.5} className={homePageStyle.featuresList}>
                                                                    {card.features.map((feature, featureIndex) => (
                                                                        <Grid item xs={12} key={featureIndex}>
                                                                            <div className={homePageStyle.featureItem}>
                                                                                <img src={circleTickIcon} alt={'img'} />
                                                                                <span style={{ fontSize: '14px' }}>{feature}</span>
                                                                            </div>
                                                                        </Grid>
                                                                    ))}
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        <div className={homePageStyle.rightColumn}>
                                                            {(card.sections).map((section, sectionIndex) => (
                                                                <div key={sectionIndex} className={homePageStyle.staticSection}>
                                                                    <div className={homePageStyle.sectionHeader}>
                                                                        <IconButton className={homePageStyle.featureCardArrowStyle}>
                                                                            <IconsColor iconName="arrowIconForCard" color={card.arrowColor} width={'18'} height={'18'} />
                                                                        </IconButton>
                                                                        <Typography className={homePageStyle.sectionTitle}>{section.title}</Typography>
                                                                    </div>
                                                                    <Typography className={homePageStyle.sectionContent}>{section.content}</Typography>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Grid> */}

                            <Grid item xl={12} lg={12} md={12} sm={12} xs={11} sx={{ margin: '0 auto', textAlign: 'center' }}>
                                <Grid className={homePageStyle.lcfcardframecontainer}>
                                    <Slider
                                        dots={true}
                                        infinite={true}
                                        autoplay={false}
                                        className={homePageStyle.chatbotclickslider}
                                        speed={400}
                                        slidesToShow={1}
                                        arrows={false}
                                        slidesToScroll={1}
                                        appendDots={appendSliderDots}
                                    >
                                        {cardData.map((card) => (
                                            <Grid container key={card.id}>
                                                <Grid item xs={11.5} sx={{ backgroundColor: card?.cardBgColor }} className={homePageStyle.homeRsCardMainItem}>
                                                    <Grid container>
                                                        <Grid item xs={12}>
                                                            <Grid container sx={{ justifyContent: "space-between", gap: "10px" }} >
                                                                <Grid item className={homePageStyle.homeRsCardIconItem}>
                                                                    {card.icon}
                                                                </Grid>
                                                                <Grid item className={homePageStyle.homeRsCardHeadItem}>
                                                                    {card.title}
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>

                                                        <Grid item xs={12} className={homePageStyle.homeRsCardDescItem}>
                                                            {card.description}
                                                        </Grid>

                                                        <Grid item xs={12}>
                                                            <div className={homePageStyle.solutionTabs}>
                                                                <Grid container sx={{ gap: "10px" }}>
                                                                    {(card.tabs || []).map((label) => (
                                                                        <Chip key={label} label={label} className={homePageStyle.solutionChip} sx={{ background: card.color }} />
                                                                    ))}
                                                                </Grid>
                                                            </div>
                                                        </Grid>

                                                        <Grid item xs={12}>
                                                            <Grid container>
                                                                <Grid item xs={12} className={homePageStyle.homeRsKeyPointItem}>
                                                                    Key Features
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <Grid container sx={{ gap: "20px" }}>
                                                                        {card.features.map((feature) => (
                                                                            <Grid item key={feature}>
                                                                                <Grid container sx={{ gap: "5px" }}>
                                                                                    <Grid item>
                                                                                        <img src={circleTickIcon} alt={'img'} />
                                                                                    </Grid>
                                                                                    <Grid item>
                                                                                        <span style={{ fontSize: '14px' }}>{feature}</span>
                                                                                    </Grid>
                                                                                </Grid>
                                                                            </Grid>
                                                                        ))}
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>

                                                        <Grid item xs={12} sx={{ margin: "20px 0px" }}>
                                                            <Divider />
                                                        </Grid>

                                                        <Grid item xs={12}>
                                                            <Grid container sx={{ gap: "15px" }}>
                                                                {card.sections?.map((section) => (
                                                                    <Grid item xs={12} key={section.title}>
                                                                        <Grid container sx={{ gap: "10px" }}>
                                                                            <Grid item className={homePageStyle.homeRscardArrowItem}>
                                                                                <IconsColor iconName="arrowIconForCard" color={card.arrowColor} width={'18'} height={'18'} />
                                                                            </Grid>
                                                                            <Grid item className={homePageStyle.homeRscardContentItem}>
                                                                                <Grid container>
                                                                                    <Grid item xs={12} className={homePageStyle.sectionTitle}>
                                                                                        {section.title}
                                                                                    </Grid>
                                                                                    <Grid item xs={12}>
                                                                                        {section.content}
                                                                                    </Grid>
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </Grid>

                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Slider>
                                </Grid>
                            </Grid>
                        </>
                    ) : (
                        <Grid item xs={12} className={homePageStyle.cardStackSection}>
                            <div ref={cardStackRef} className={homePageStyle.cardStackContainer}>
                                {cardData.map((card) => (
                                    <div
                                        key={card.id}
                                        ref={el => {
                                            const idx = cardData.findIndex(c => c.id === card.id)
                                            cardRefs.current[idx] = el
                                        }}
                                        className={homePageStyle.cardItem}
                                    >
                                        <div className={homePageStyle.card}>
                                            <div className={homePageStyle.cardHeader}>
                                                <div className={homePageStyle.cardIcon}>{card.icon}</div>
                                                <Grid container className={homePageStyle.cardTitleSection}>
                                                    <Grid item xs={12}>
                                                        <Typography className={homePageStyle.cardTitle}>{card.title}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Typography className={homePageStyle.cardDescription}>{card.description}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </div>
                                            <div className={homePageStyle.solutionTabs}>
                                                {(card.tabs || []).map((label) => (
                                                    <Chip key={label} label={label} className={homePageStyle.solutionChip} sx={{ background: card.color }} />
                                                ))}
                                            </div>
                                            <div className={homePageStyle.cardContent}>
                                                <Grid container className={homePageStyle.cardContent}>
                                                    <Grid item xs={12} className={homePageStyle.leftColumn}>
                                                        <Typography className={homePageStyle.featuresTitle}>Key Features</Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Grid container spacing={1.5} className={homePageStyle.featuresList}>
                                                            {card.features.map((feature) => (
                                                                <Grid item xs={12} key={feature}>
                                                                    <div className={homePageStyle.featureItem}>
                                                                        <img src={circleTickIcon} alt={'img'} />
                                                                        <span style={{ fontSize: '14px' }}>{feature}</span>
                                                                    </div>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <div className={homePageStyle.rightColumn}>
                                                    {(card.sections).map((section) => (
                                                        <div key={section.title} className={homePageStyle.staticSection}>
                                                            <div className={homePageStyle.sectionHeader}>
                                                                <IconButton className={homePageStyle.featureCardArrowStyle}>
                                                                    <IconsColor iconName="arrowIconForCard" color={card.arrowColor} width={'18'} height={'18'} />
                                                                </IconButton>
                                                                <Typography className={homePageStyle.sectionTitle}>{section.title}</Typography>
                                                            </div>
                                                            <Typography className={homePageStyle.sectionContent}>{section.content}</Typography>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Grid>
                    )}
                </Grid>
            </Grid>

            {/* Explore with Quick Videos */}
            {/* <Grid item xs={12} className={homePageStyle.videosSectionContainer}>
                    <Grid container gap={"20px"} className={homePageStyle.sectionInner}>
                        <Grid item xs={9} className={homePageStyle.videosHeaderRow}>
                            <Typography className={homePageStyle.videosTitle}>Explore with Quick Videos</Typography>
                            <div className={homePageStyle.videosNavBtns}>
                                <IconButton size="small" className={homePageStyle.navIconBtn} disabled={!canScrollLeft} onClick={() => scrollVideos('left')}>
                                    <ChevronLeftRoundedIcon />
                                </IconButton>
                                <IconButton size="small" className={homePageStyle.navIconBtnPrimary} disabled={!canScrollRight} onClick={() => scrollVideos('right')}>
                                    <ChevronRightRoundedIcon />
                                </IconButton>
                            </div>
                        </Grid>
                        <Grid item xs={9}>
                            <div className={homePageStyle.videosTrackWrapper}>
                                <div className={homePageStyle.videosTrack} ref={videosTrackRef}>
                                    {videos.map(v => (
                                        <div key={v.id} className={homePageStyle.videoCardItem}>
                                            <div className={homePageStyle.videoThumbLarge}>
                                                <PlayCircleOutlineIcon className={homePageStyle.videoPlayIcon} />
                                            </div>
                                            <div className={homePageStyle.videoMetaRow}>
                                                <span className={homePageStyle.videoBadge}>{v.order}</span>
                                            </div>
                                            <Typography className={homePageStyle.videoTitle}>{v.title}</Typography>
                                            <div className={homePageStyle.videoActions}>
                                                <Button className={homePageStyle.watchBtn} variant="contained">Watch Video</Button>
                                                <Button className={homePageStyle.learnBtn} variant="outlined">Learn More</Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Grid> */}

            {/* Contact us banner */}
            <Grid item xs={12} className={homePageStyle.contactSection}>
                <Grid container className={homePageStyle.contactContainer}>
                    <Grid item xs={12}>
                        <img src={unfilledBrainLogo} alt={"rbLogo"} width={"60px"} height={"50px"} />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={12}>
                                <Typography className={homePageStyle.contactSubtitle}>GET IN TOUCH</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography className={homePageStyle.contactTitle}>
                                    Contact us today to learn how {BRAND_NAME} can help you serve your clients better.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container className={homePageStyle.contactButtonWrapper}>
                            <Grid item>
                                <Button
                                    className={homePageStyle.contactButton}
                                    onClick={handleScheduleDemoBtnClick}
                                >
                                    Schedule a Demo
                                </Button>
                            </Grid>
                            {/* <Grid item>
                                    <Button
                                        className={homePageStyle.learnMoreCtaButtom}
                                    >
                                        Learn More
                                    </Button>
                                </Grid> */}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

        </Grid>
    )
}

export default HomePage